import React, { useState, useEffect, forwardRef, useImperativeHandle, useCallback } from 'react';

interface MathCaptchaProps {
  value: string;
  onChange: (value: string) => void;
  onValidChange?: (isValid: boolean) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  error?: boolean;
  label?: string;
  required?: boolean;
}

export interface MathCaptchaRef {
  resetCaptcha: () => void;
}

const MathCaptcha = forwardRef<MathCaptchaRef, MathCaptchaProps>(({
  value,
  onChange,
  onValidChange,
  placeholder = 'Ответ',
  disabled = false,
  className = '',
  error = false,
  label = 'Решите пример',
  required = false
}, ref) => {
  const [num1, setNum1] = useState(0);
  const [num2, setNum2] = useState(0);
  const [captchaValue, setCaptchaValue] = useState(value);

  useEffect(() => {
    setCaptchaValue(value);
  }, [value]);

  const generateNewCaptcha = useCallback(() => {
    setNum1(Math.floor(Math.random() * 10));
    setNum2(Math.floor(Math.random() * 10));
    setCaptchaValue('');
    onChange('');
    if (onValidChange) {
      onValidChange(false);
    }
  }, [onChange, onValidChange]);

  // Генерируем капчу только при первом рендере
  useEffect(() => {
    generateNewCaptcha();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useImperativeHandle(ref, () => ({
    resetCaptcha: generateNewCaptcha
  }), [generateNewCaptcha]);

  const handleCaptchaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    
    // Разрешаем только цифры
    const digitsOnly = inputValue.replace(/\D/g, '');
    
    setCaptchaValue(digitsOnly);
    onChange(digitsOnly);

    // Проверяем правильность ответа
    if (onValidChange) {
      const correctAnswer = num1 + num2;
      const newIsValid = parseInt(digitsOnly) === correctAnswer;
      onValidChange(newIsValid);
    }
  };

  const handleCaptchaKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Backspace, Delete, Tab, Escape, Enter
    if (['Backspace', 'Delete', 'Tab', 'Escape', 'Enter'].includes(e.key)) {
      return;
    }
    
    // Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
    if ((e.ctrlKey || e.metaKey) && ['a', 'c', 'v', 'x'].includes(e.key.toLowerCase())) {
      return;
    }
    
    // Home, End, Arrow keys
    if (['Home', 'End', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
      return;
    }

    // Разрешаем только цифры
    if (!/^\d$/.test(e.key)) {
      e.preventDefault();
    }
  };

  const handleCaptchaPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    const digitsOnly = pastedText.replace(/\D/g, '');
    setCaptchaValue(digitsOnly);
    onChange(digitsOnly);

    // Проверяем правильность ответа
    if (onValidChange) {
      const correctAnswer = num1 + num2;
      const newIsValid = parseInt(digitsOnly) === correctAnswer;
      onValidChange(newIsValid);
    }
  };

  const correctAnswer = num1 + num2;
  const isAnswerValid = captchaValue !== '' && parseInt(captchaValue) === correctAnswer;
  const showError = error || (captchaValue !== '' && !isAnswerValid);

  return (
    <div className={className}>
      {label && (
        <label className="block text-sm text-white mb-2">
          {label}: {num1} + {num2} = ?
          {required && <span className="text-red-400 ml-1">*</span>}
        </label>
      )}
      <input
        type="text"
        value={captchaValue}
        onChange={handleCaptchaChange}
        onKeyDown={handleCaptchaKeyDown}
        onPaste={handleCaptchaPaste}
        placeholder="Введите ответ"
        disabled={disabled}
        className={`
          w-full px-4 py-3 border rounded-2xl text-lg
          focus:outline-none focus:ring-2 focus:ring-white/40 focus:border-transparent
          transition-all duration-200
          ${showError 
            ? 'border-red-500/30 focus:ring-red-500/30' 
            : 'border-white/20 focus:border-white/40'
          }
          ${disabled 
            ? 'bg-white/10 text-white/50 cursor-not-allowed' 
            : 'bg-white/10 text-white border-white/20'
          }
        `}
        inputMode="numeric"
        autoComplete="off"
        maxLength={3}
      />
      
      {/* Сообщение об ошибке */}
      {captchaValue !== '' && !isAnswerValid && (
        <div className="mt-1 text-sm text-red-400">
          Неверный ответ
        </div>
      )}
    </div>
  );
});

MathCaptcha.displayName = 'MathCaptcha';

export default MathCaptcha;
